import {Component, inject, Input, OnDestroy} from '@angular/core';
import {Subject, takeUntil} from "rxjs";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from "@angular/material/card";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatRadioModule} from "@angular/material/radio";
import {MatSelectModule} from "@angular/material/select";
import {IGameQueryResult} from "../../interfaces/IGameQueryResult";
import {IDropdownOption} from "../../interfaces/IDropDownOptions";
import {IGamePayoutRule} from "../../interfaces/payout-rule/IGamePayoutRule";
import {IGamePayoutPayableComponent} from "../../interfaces/payout-rule/IGamePayoutPayableComponent";
import {ISetGamePayoutRuleRecipient} from "../../interfaces/commands/ISetGamePayoutRuleCommand";
import {GamePayoutRuleService} from "../../services/game-payout-rule.service";
import {CharityService} from "../../services/charity.service";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {SnackbarService} from "../../services/snackbar.service";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {LoadingCardContentComponent} from "../../shared/loading-card-content/loading-card-content.component";
import {HasPermissionDirective} from "../../directives/permissions/has-permission";
import {PermissionTypes} from "../../enum/PermissionTypes";
import {MatDialog} from "@angular/material/dialog";
import {PayoutRulesHelpComponent} from "../../components/dialogs/payout-rules-help/payout-rules-help.component";
import {PAYABLE_COMPONENT_LABELS, PayableComponentTypes} from "../../enum/PayableComponentTypes";
import {TableExportButtonsComponent} from "../../components/table-export-buttons/table-export-buttons.component";
import {ITableExportRequest, TableExportService} from "../../services/table-export.service";
import {ErrorTranslationService} from "../../services/error-translation.service";
import {
  ConfirmPayoutRuleDateDialogComponent,
  IConfirmPayoutRuleDateData
} from "../../components/dialogs/confirm-payout-rule-date/confirm-payout-rule-date.component";
import {
  IVoidPayoutRuleVersionData,
  VoidPayoutRuleVersionDialogComponent
} from "../../components/dialogs/void-payout-rule-version/void-payout-rule-version.component";

type PayeeType = 'charity' | 'external';

interface IPayableComponentForm {
  componentType: FormControl<number>;
  included: FormControl<boolean>;
  isAddition: FormControl<boolean>;
}

interface IRecipientForm {
  payeeType: FormControl<PayeeType>;
  charityId: FormControl<string | null>;
  externalName: FormControl<string | null>;
  sharePercent: FormControl<number | null>;
}

/**
 * Per-organization payout rules for one game (Phase 5 of retiring the Google-Sheets payout ledgers).
 *
 * Two settings, both presentation over figures the reporting engine already returns: a host admin fee
 * (whose recipients can be split, as SYSPEI splits 50/50 between Sport PEI and North River Minor Hockey)
 * and a net amount payable to the host organization (UPEI to itself; UNITE and OK to a charity that is
 * already in their game's list).
 *
 * The share arithmetic is checked here as well as server-side, because being told "the shares total 90%"
 * while looking at the rows is worth more than being told it after a failed save. The server is still the
 * authority — this is a courtesy, not the guard.
 */
@Component({
  selector: 'app-payout-rules',
  standalone: true,
  imports: [
    TableExportButtonsComponent,
    ReactiveFormsModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatCheckboxModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    MatIconButton,
    MatIcon,
    MatRadioModule,
    MatSelectModule,
    LoadingCardContentComponent,
    HasPermissionDirective
  ],
  templateUrl: './payout-rules.component.html',
  styleUrls: ['./payout-rules.component.scss']
})
export class PayoutRulesComponent implements OnDestroy {

  private gamePayoutRuleService: GamePayoutRuleService = inject(GamePayoutRuleService);
  private tableExportService: TableExportService = inject(TableExportService);
  private charityService: CharityService = inject(CharityService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private errorTranslationService: ErrorTranslationService = inject(ErrorTranslationService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private matDialog: MatDialog = inject(MatDialog);
  private destroy$ = new Subject<void>();

  @Input() set gameData(gameP: IGameQueryResult) {
    this.activeGame = gameP;
    this.charityOptions = [];
    this.errorTranslationService.load();
    this.fetchCharitiesForGame();
    this.refresh();
  }

  public activeGame: IGameQueryResult | null = null;
  public charityOptions: IDropdownOption[] = [];
  /** Every version of the rule, newest first — the change history, so a date field can be checked against it. */
  public versions: IGamePayoutRule[] = [];

  /**
   * The version the form was loaded from, or null when a new one is being added.
   *
   * The save is identified by this rather than by the date on screen (#1411 review). Otherwise the date field
   * silently decides which version is written: open the version effective Jun 1, nudge the date to May 1 without
   * noticing, save — and a new May 1 version appears while the June one stays as it was, with nothing saying so.
   */
  public editingVersionId: string | null = null;
  /**
   * The game's administration fee rate, as sent alongside the rule. Zero until the fetch lands, which is why
   * the whole card is gated on the loading state — a rate of 0 reads as "this game charges no fee".
   */
  public administrationCommissionMultiplier = 0;
  public isSubmitting = false;
  /**
   * Starts as loading, and the whole form is gated on it. Starting as ShowData left a window between the
   * component being created and the rule arriving where the form was already interactive — and when the
   * response landed, applyRuleToForm reset it, silently discarding whatever had been typed.
   */
  public uiState = UIStateEnum.ShowLoading;
  protected readonly UIStateEnum = UIStateEnum;
  protected readonly permissionTypes = PermissionTypes;

  public payoutRuleForm = new FormGroup({
    /**
     * When this version of the rule takes effect (#1411). Defaults to today, so an admin who does not think about
     * dating gets "from now on" and every period already reported keeps the rule that was in force then.
     */
    effectiveFrom: new FormControl<string>(PayoutRulesComponent.todayIso(), {nonNullable: true}),
    hostAdminFeeEnabled: new FormControl<boolean>(false, {nonNullable: true}),
    adminFeeRecipients: new FormArray<FormGroup<IRecipientForm>>([]),
    hostPayableEnabled: new FormControl<boolean>(false, {nonNullable: true}),
    hostPayablePayeeType: new FormControl<PayeeType>('external', {nonNullable: true}),
    hostPayableCharityId: new FormControl<string | null>(null),
    hostPayableExternalName: new FormControl<string | null>(null),
    // One row per figure the payable can include: whether it is in, and whether it adds or subtracts.
    payableComponents: new FormArray<FormGroup<IPayableComponentForm>>([])
  });

  /**
   * The longer explanation, with worked examples, lives here rather than on the page: the sections need one
   * plain line each, and "what the payment is made of" only becomes concrete once you see three real shapes.
   */
  openHelp(): void {
    this.matDialog.open(PayoutRulesHelpComponent, {width: '760px', maxHeight: '85vh'});
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get recipients(): FormArray<FormGroup<IRecipientForm>> {
    return this.payoutRuleForm.controls.adminFeeRecipients;
  }

  get payableComponents(): FormArray<FormGroup<IPayableComponentForm>> {
    return this.payoutRuleForm.controls.payableComponents;
  }

  /** The rows the admin has ticked — what the payable will actually be made of. */
  get includedComponents(): FormGroup<IPayableComponentForm>[] {
    return this.payableComponents.controls.filter(cP => cP.controls.included.value);
  }

  componentLabel(componentTypeP: number): string {
    return PAYABLE_COMPONENT_LABELS.find(lP => lP.type === componentTypeP)?.label ?? `Figure ${componentTypeP}`;
  }

  get hostAdminFeeEnabled(): boolean {
    return this.payoutRuleForm.controls.hostAdminFeeEnabled.value;
  }

  get hostPayableEnabled(): boolean {
    return this.payoutRuleForm.controls.hostPayableEnabled.value;
  }

  get hostPayablePayeeType(): PayeeType {
    return this.payoutRuleForm.controls.hostPayablePayeeType.value;
  }

  /**
   * The administration fee this game already charges, as a percentage of every payment. Read from the
   * payout-rule endpoint, which carries the game's multiplier (0.01 is 1%) — the same figure the live payment
   * path charges, via PaymentHandlersService handing Game.AdministrationCommissionMultiplier to
   * PaymentCalculator.
   *
   * Not read off the game this page is scoped to: IGameQueryResult declares the field, but the API's
   * GameQueryResult does not carry it — that DTO is also served by the unauthenticated by-subdomain endpoint,
   * so fee rates are deliberately kept out of it. Trusting the interface here left the screen permanently
   * claiming every game charges 0%.
   *
   * The shares below are shares of *this*, and without it on screen "the shares must total 100%" has no
   * answerable subject: 100% of what? (#1411). A host taking 0.5% of every payment out of a 1% fee has a
   * share of 50, and there is no way to know that from a column headed "Share %" alone.
   */
  get feePercentOfEveryPayment(): number {
    return PayoutRulesComponent.roundToFour(this.administrationCommissionMultiplier * 100);
  }

  get gameChargesAnAdministrationFee(): boolean {
    return this.feePercentOfEveryPayment > 0;
  }

  /**
   * What one row's share works out to as a percentage of every payment — the unit organizations state their
   * fee in ("SYSPEI takes 0.5% of sales"), rather than the unit the form is entered in.
   * Null when the row has no share yet, or when the game charges no fee for the share to be a share of.
   */
  recipientPercentOfEveryPayment(recipientP: FormGroup<IRecipientForm>): number | null {
    const share = PayoutRulesComponent.roundToTwo(recipientP.controls.sharePercent.value ?? 0);

    if (share <= 0 || !this.gameChargesAnAdministrationFee) {
      return null;
    }

    return PayoutRulesComponent.roundToFour(this.feePercentOfEveryPayment * share / 100);
  }

  /**
   * Percentages the admin typed, printed back without inventing precision: a 1% fee split three ways is
   * 0.3333% each, while a 10% fee split in half is 5% and not 5.0000%.
   */
  formatPercent(valueP: number): string {
    return `${PayoutRulesComponent.roundToFour(valueP)}`;
  }

  /**
   * Rounded to the two decimal places the share column stores. Totalling the raw entries and rounding the
   * total would read 33.333 three times as 100% and then store three 33.33s totalling 99.99.
   */
  get sharesTotal(): number {
    return this.recipients.controls.reduce((totalP, recipientP) =>
      totalP + PayoutRulesComponent.roundToTwo(recipientP.controls.sharePercent.value ?? 0), 0);
  }

  get sharesTotalOneHundred(): boolean {
    return PayoutRulesComponent.roundToTwo(this.sharesTotal) === 100;
  }

  get hasRecipientMissingAPayee(): boolean {
    return this.recipients.controls.some(recipientP => this.payeeKey(recipientP) === null);
  }

  get hasShareOutOfRange(): boolean {
    return this.recipients.controls.some(recipientP => {
      const share = PayoutRulesComponent.roundToTwo(recipientP.controls.sharePercent.value ?? 0);

      return share <= 0 || share > 100;
    });
  }

  /** The same payee twice reads as two amounts owed to one organization, which invites paying it twice. */
  get hasDuplicateRecipient(): boolean {
    const keys = this.recipients.controls
      .map(recipientP => this.payeeKey(recipientP))
      .filter((keyP): keyP is string => keyP !== null);

    return new Set(keys).size !== keys.length;
  }

  get payableIsMissingItsPayee(): boolean {
    if (!this.hostPayableEnabled) {
      return false;
    }

    return this.hostPayablePayeeType === 'charity'
      ? !this.payoutRuleForm.controls.hostPayableCharityId.value
      : !this.payoutRuleForm.controls.hostPayableExternalName.value?.trim();
  }

  /**
   * The single reason the save button is disabled, or null when it is not. One message at a time: a list of
   * everything wrong at once reads as noise on a form this small.
   */
  get blockingMessage(): string | null {
    if (this.hostAdminFeeEnabled) {
      if (this.recipients.length === 0) {
        return 'Add at least one recipient for the host admin fee, or turn the fee off.';
      }

      if (this.hasRecipientMissingAPayee) {
        return 'Every recipient needs a payee: either a charity in this game, or a named outside organization.';
      }

      if (this.hasShareOutOfRange) {
        return 'Each share must be greater than 0% and at most 100%.';
      }

      if (this.hasDuplicateRecipient) {
        return 'The same recipient is listed twice. Combine the two shares into one line.';
      }

      if (!this.sharesTotalOneHundred) {
        // Says what the 100% is 100% of, and converts the mistake that produces this message: shares typed as
        // percentages of sales rather than of the fee (#1411).
        return this.gameChargesAnAdministrationFee
          ? `The shares total ${this.sharesTotal.toFixed(2)}% and must total exactly 100%. `
            + `They divide the one ${this.formatPercent(this.feePercentOfEveryPayment)}% administration fee this game already charges, `
            + `so a recipient taking half of it — ${this.formatPercent(this.feePercentOfEveryPayment / 2)}% of every payment — has a share of 50, not ${this.formatPercent(this.feePercentOfEveryPayment / 2)}.`
          : `The shares total ${this.sharesTotal.toFixed(2)}% and must total exactly 100%. `
            + `They divide the administration fee already collected on payments, not the sales — and this game's fee is 0% at the moment, so 100% is whatever was collected while a fee was charged.`;
      }
    }

    if (this.payableIsMissingItsPayee) {
      return 'Choose who the net amount is payable to, or turn the payable off.';
    }

    if (this.hostPayableEnabled && this.includedComponents.length === 0) {
      return 'Choose at least one figure the payable is made of, or turn the payable off.';
    }

    const occupied = this.versionAlreadyOnChosenDate;

    if (occupied) {
      // Refused rather than silently editing that version with what is on screen: the version on that date says what it
      // says, and periods have been reported under it.
      return `A version of these rules already takes effect on ${this.formatEffectiveFrom(occupied.EffectiveFrom)}.`
        + ' Open it under Change history to edit it, or choose another date.';
    }

    if (this.movingTheVersion && this.payoutRuleForm.controls.effectiveFrom.value <= PayoutRulesComponent.todayIso()) {
      return 'A version can only move to a date after today. Moving it to today or earlier would change periods already'
        + ' reported. To correct a past date, a Causable admin can void the version, and you can then add one on the right date.';
    }

    return null;
  }

  /** Loads a past or future version into the form, so it can be read or corrected on its own terms. */
  loadVersion(versionP: IGamePayoutRule): void {
    this.applyRuleToForm(versionP);
  }

  /**
   * Switches from editing a version to adding one, keeping what is on screen as the starting point — the normal
   * way to change a rule going forward.
   *
   * This is the only route to a writable date. While a version is being edited the date belongs to that version
   * and is shown, not asked for: typing a different date there used to mean "save this somewhere else", which is
   * not a thing an admin correcting a version is trying to say.
   */
  startNewVersion(): void {
    this.editingVersionId = null;
    this.payoutRuleForm.controls.effectiveFrom.setValue(PayoutRulesComponent.todayIso());
    this.payoutRuleForm.controls.effectiveFrom.enable();
  }

  /** The version a save would write, for the line above the button that does it. */
  get versionBeingEdited(): IGamePayoutRule | null {
    return this.versions.find(versionP => versionP.Id === this.editingVersionId) ?? null;
  }

  /**
   * The date the save will carry. Taken from the version being edited where there is one, since that is what the
   * server will keep — and from the form only while adding, which is the only time the field is an input. Falls
   * back to the form so the line above the save button cannot go blank if the history has not loaded.
   */
  get dateBeingSaved(): string {
    if (this.addingAVersion || this.movingTheVersion) {
      return this.payoutRuleForm.controls.effectiveFrom.value;
    }

    return this.versionBeingEdited?.EffectiveFrom ?? this.payoutRuleForm.controls.effectiveFrom.value;
  }

  /** True when an edit carries a new date for a version that has not taken effect yet. */
  get movingTheVersion(): boolean {
    const version = this.versionBeingEdited;

    return !!version
      && this.payoutRuleForm.controls.effectiveFrom.enabled
      && this.payoutRuleForm.controls.effectiveFrom.value !== PayoutRulesComponent.calendarDate(version.EffectiveFrom);
  }

  /**
   * The version, if any, that already starts on the date chosen for a new one. Adding onto an occupied date is
   * refused: overwriting would replace what that version says with the values on screen, restating every period
   * reported since.
   */
  get versionAlreadyOnChosenDate(): IGamePayoutRule | null {
    if (!this.addingAVersion && !this.movingTheVersion) {
      return null;
    }

    const chosen = this.payoutRuleForm.controls.effectiveFrom.value;

    // A voided version gives its date up, so the corrected version can take it.
    return this.versions.find(versionP => !this.isVoided(versionP)
      && versionP.Id !== this.editingVersionId
      && PayoutRulesComponent.calendarDate(versionP.EffectiveFrom) === chosen) ?? null;
  }

  /**
   * A version's effective date, formatted without a timezone conversion.
   *
   * Never through DatePipe: effective_from is a calendar date, which the API serializes as UTC midnight, so any
   * conversion renders the previous day for an admin west of UTC. A rule that reads as taking effect a day earlier
   * than it does is exactly the ambiguity effective dating exists to remove — the same trap the payout ledger's
   * payment dates hit.
   */
  formatEffectiveFrom(valueP: string | null | undefined): string {
    if (!valueP) {
      return '—';
    }

    const [year, month, day] = valueP.split('T')[0].split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return `${months[Number(month) - 1]} ${Number(day)}, ${year}`;
  }

  /** A version's shares, for the history line, so the change between versions is readable at a glance. */
  versionSummary(versionP: IGamePayoutRule): string {
    if (!versionP.HostAdminFeeEnabled) {
      return 'No host admin fee';
    }

    const shares = (versionP.AdminFeeRecipients ?? []).map(recipientP => `${recipientP.SharePercent}%`);

    return shares.length ? `Fee split ${shares.join(' / ')}` : 'Host admin fee, no recipients';
  }

  isVersionBeingEdited(versionP: IGamePayoutRule): boolean {
    return versionP.Id === this.editingVersionId;
  }

  isVoided(versionP: IGamePayoutRule): boolean {
    return !!versionP.VoidedOn;
  }

  /** When a version was voided, in the admin's own calendar: it is a moment, not a calendar date like EffectiveFrom. */
  formatVoidedOn(valueP: string | null | undefined): string {
    if (!valueP) {
      return '—';
    }

    const utc = /[zZ]$|[+-]\d{2}:\d{2}$/.test(valueP) ? valueP : `${valueP}Z`;

    return new Date(utc).toLocaleDateString('en-CA', {year: 'numeric', month: 'short', day: 'numeric'});
  }

  voidVersion(versionP: IGamePayoutRule): void {
    if (!this.activeGame) return;

    const game_id = this.activeGame.Id;

    this.matDialog.open<VoidPayoutRuleVersionDialogComponent, IVoidPayoutRuleVersionData, string | false>(
      VoidPayoutRuleVersionDialogComponent,
      {width: '560px', data: {effectiveFrom: this.formatEffectiveFrom(versionP.EffectiveFrom)}}
    ).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(reasonP => {
      if (!reasonP) {
        return;
      }

      this.gamePayoutRuleService.voidPayoutRule({GameId: game_id, GamePayoutRuleId: versionP.Id, Reason: reasonP})
        .pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.snackbarService.openSuccessfulSnackBar('Payout rule version voided');
            this.refresh();
          },
          error: (err) => {
            this.errorHandlingService.displayPageLevelCommandErrorMessage(err, this.errorTranslationService.messageFor(err));
          }
        });
    });
  }

  get editingAFutureVersion(): boolean {
    return this.payoutRuleForm.controls.effectiveFrom.value > PayoutRulesComponent.todayIso();
  }

  get editingAPastVersion(): boolean {
    return this.payoutRuleForm.controls.effectiveFrom.value < PayoutRulesComponent.todayIso();
  }

  /** True while adding a version, which is the only state in which the date is an input. */
  get addingAVersion(): boolean {
    return this.editingVersionId === null;
  }

  get canSave(): boolean {
    return this.blockingMessage === null && !this.isSubmitting;
  }

  refresh(): void {
    if (!this.activeGame) return;

    this.uiState = UIStateEnum.ShowLoading;

    this.gamePayoutRuleService.fetchByGameId(this.activeGame.Id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resultP) => {
        this.administrationCommissionMultiplier = resultP?.AdministrationCommissionMultiplier ?? 0;
        this.versions = resultP?.Versions ?? [];
        this.applyRuleToForm(resultP?.Rule ?? null);
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  addRecipient(): void {
    this.recipients.push(PayoutRulesComponent.newRecipientForm());
  }

  removeRecipient(indexP: number): void {
    this.recipients.removeAt(indexP);
  }

  /**
   * Clearing the other payee field when the type changes keeps the form honest: leaving a charity selected
   * behind an "outside organization" radio is how a save ends up naming a payee nobody chose.
   */
  onRecipientPayeeTypeChange(recipientP: FormGroup<IRecipientForm>): void {
    if (recipientP.controls.payeeType.value === 'charity') {
      recipientP.controls.externalName.setValue(null);
    } else {
      recipientP.controls.charityId.setValue(null);
    }
  }

  onPayablePayeeTypeChange(): void {
    if (this.hostPayablePayeeType === 'charity') {
      this.payoutRuleForm.controls.hostPayableExternalName.setValue(null);
    } else {
      this.payoutRuleForm.controls.hostPayableCharityId.setValue(null);
    }
  }

  /** A new date is read back with its weekday first: "Friday, May 1" catches May typed for June where 2026-05-01 doesn't. */
  onSubmit(): void {
    if (!this.canSave || !this.activeGame) {
      return;
    }

    if (!this.addingAVersion && !this.movingTheVersion) {
      this.save();
      return;
    }

    const new_date = PayoutRulesComponent.longDate(this.payoutRuleForm.controls.effectiveFrom.value);
    const data: IConfirmPayoutRuleDateData = this.movingTheVersion
      ? {
          title: 'Move this version?',
          question: `Move this version from ${PayoutRulesComponent.longDate(this.versionBeingEdited!.EffectiveFrom)} to ${new_date}?`
        }
      : {title: 'Add this version?', question: `Add a version in effect from ${new_date}?`};

    this.matDialog.open<ConfirmPayoutRuleDateDialogComponent, IConfirmPayoutRuleDateData, boolean>(
      ConfirmPayoutRuleDateDialogComponent, {width: '480px', data}
    ).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmedP => {
      if (confirmedP) {
        this.save();
      }
    });
  }

  private save(): void {
    if (!this.activeGame) {
      return;
    }

    this.isSubmitting = true;

    const form_value = this.payoutRuleForm.getRawValue();
    const fee_enabled = form_value.hostAdminFeeEnabled;
    const payable_enabled = form_value.hostPayableEnabled;

    this.gamePayoutRuleService.setPayoutRule({
      GameId: this.activeGame.Id,
      // The version being edited, so the save cannot land on a different one. Null adds a version. The date is sent
      // only when adding or moving one, so an ordinary edit can never re-date a version.
      GamePayoutRuleId: this.editingVersionId,
      EffectiveFrom: this.addingAVersion || this.movingTheVersion ? form_value.effectiveFrom || null : null,
      HostAdminFeeEnabled: fee_enabled,
      // Only sent when the fee is on. The server drops them otherwise, but sending a list the admin
      // cannot see would leave the request describing something other than the screen.
      AdminFeeRecipients: fee_enabled ? this.buildRecipientPayload() : [],
      HostPayableEnabled: payable_enabled,
      HostPayableCharityId: payable_enabled && form_value.hostPayablePayeeType === 'charity'
        ? form_value.hostPayableCharityId
        : null,
      HostPayableExternalName: payable_enabled && form_value.hostPayablePayeeType === 'external'
        ? form_value.hostPayableExternalName?.trim() || null
        : null,
      // Only the ticked rows are sent, in the order they are shown, so the request describes the screen.
      PayableComponents: payable_enabled
        ? this.includedComponents.map(cP => ({
            ComponentType: cP.controls.componentType.value,
            IsAddition: cP.controls.isAddition.value
          }))
        : []
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackbarService.openSuccessfulSnackBar('Payout rules saved successfully!');
        this.isSubmitting = false;
        this.refresh();
      },
      error: (err) => {
        // Reset before reporting: the error helper rethrows, so nothing after it runs.
        this.isSubmitting = false;
        this.errorHandlingService.displayPageLevelCommandErrorMessage(err, this.errorTranslationService.messageFor(err));
      }
    });
  }

  private buildRecipientPayload(): ISetGamePayoutRuleRecipient[] {
    return this.recipients.controls.map(recipientP => {
      const is_charity = recipientP.controls.payeeType.value === 'charity';

      return {
        CharityId: is_charity ? recipientP.controls.charityId.value : null,
        ExternalName: is_charity ? null : recipientP.controls.externalName.value?.trim() || null,
        SharePercent: PayoutRulesComponent.roundToTwo(recipientP.controls.sharePercent.value ?? 0)
      };
    });
  }

  private applyRuleToForm(ruleP: IGamePayoutRule | null): void {
    this.recipients.clear();

    this.editingVersionId = ruleP?.Id ?? null;

    // Disabled, not readonly, for a version in force: moving it would restate periods already reported.
    if (this.editingVersionId && PayoutRulesComponent.calendarDate(ruleP!.EffectiveFrom) <= PayoutRulesComponent.todayIso()) {
      this.payoutRuleForm.controls.effectiveFrom.disable();
    } else {
      this.payoutRuleForm.controls.effectiveFrom.enable();
    }

    if (!ruleP) {
      // No rule configured: neither a fee nor a payable, which is COS PEI's shape and the default for a
      // game nobody has configured yet.
      this.payoutRuleForm.reset({
        effectiveFrom: PayoutRulesComponent.todayIso(),
        hostAdminFeeEnabled: false,
        hostPayableEnabled: false,
        hostPayablePayeeType: 'external',
        hostPayableCharityId: null,
        hostPayableExternalName: null
      });

      this.rebuildComponentRows([]);

      return;
    }

    this.payoutRuleForm.patchValue({
      // The date of the version being edited, not today: an admin correcting last month's version must not have
      // their fix silently re-dated to now, which would leave the wrong figures standing for last month.
      effectiveFrom: ruleP.EffectiveFrom ? PayoutRulesComponent.calendarDate(ruleP.EffectiveFrom) : PayoutRulesComponent.todayIso(),
      hostAdminFeeEnabled: ruleP.HostAdminFeeEnabled,
      hostPayableEnabled: ruleP.HostPayableEnabled,
      hostPayablePayeeType: ruleP.HostPayableCharityId ? 'charity' : 'external',
      hostPayableCharityId: ruleP.HostPayableCharityId,
      hostPayableExternalName: ruleP.HostPayableExternalName
    });

    this.rebuildComponentRows(ruleP.PayableComponents ?? []);

    for (const recipient of ruleP.AdminFeeRecipients) {
      const recipient_form = PayoutRulesComponent.newRecipientForm();

      recipient_form.patchValue({
        payeeType: recipient.CharityId ? 'charity' : 'external',
        charityId: recipient.CharityId,
        externalName: recipient.ExternalName,
        sharePercent: recipient.SharePercent
      });

      this.recipients.push(recipient_form);
    }
  }

  /**
   * One row per available figure, ticked and signed to match the stored rule. Every figure is listed even when
   * unused, so choosing what a payable is made of is a matter of ticking boxes rather than knowing the
   * vocabulary — and the stored order is preserved for the ones already in use.
   */
  private rebuildComponentRows(storedP: IGamePayoutPayableComponent[]): void {
    this.payableComponents.clear();

    const stored_in_order = [...storedP].sort((aP, bP) => aP.SortOrder - bP.SortOrder);
    const ordered_types = [
      ...stored_in_order.map(cP => cP.ComponentType),
      ...PAYABLE_COMPONENT_LABELS.map(lP => lP.type as number).filter(tP => !stored_in_order.some(cP => cP.ComponentType === tP))
    ];

    for (const component_type of ordered_types) {
      const stored = stored_in_order.find(cP => cP.ComponentType === component_type);

      this.payableComponents.push(new FormGroup<IPayableComponentForm>({
        componentType: new FormControl<number>(component_type, {nonNullable: true}),
        included: new FormControl<boolean>(!!stored, {nonNullable: true}),
        isAddition: new FormControl<boolean>(stored ? stored.IsAddition : true, {nonNullable: true})
      }));
    }
  }

  private fetchCharitiesForGame(): void {
    if (!this.activeGame) return;

    // Same source the charity payout form uses, so a charity that appears there is choosable here too.
    this.charityService.fetchCharitiesPerGameByCategory(this.activeGame.Id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (categoriesP) => {
        this.charityOptions = categoriesP
          .flatMap(categoryP => categoryP.Charities)
          .map(charityP => ({id: charityP.Id, name: charityP.Name}));
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  /**
   * A payee's identity for duplicate detection. Outside names are compared case-insensitively and trimmed:
   * "Sport PEI" and "SPORT PEI " are one organization, and splitting a fee between a payee and itself is
   * not a split.
   */
  private payeeKey(recipientP: FormGroup<IRecipientForm>): string | null {
    if (recipientP.controls.payeeType.value === 'charity') {
      const charity_id = recipientP.controls.charityId.value;

      return charity_id ? `charity:${charity_id}` : null;
    }

    const external_name = recipientP.controls.externalName.value?.trim();

    return external_name ? `external:${external_name.toLowerCase()}` : null;
  }

  private static newRecipientForm(): FormGroup<IRecipientForm> {
    return new FormGroup<IRecipientForm>({
      payeeType: new FormControl<PayeeType>('charity', {nonNullable: true}),
      charityId: new FormControl<string | null>(null),
      externalName: new FormControl<string | null>(null),
      sharePercent: new FormControl<number | null>(null)
    });
  }

  /** Today as yyyy-MM-dd in the admin's own calendar; toISOString would roll over a day west of UTC. */
  private static todayIso(): string {
    const now = new Date();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');

    return `${now.getFullYear()}-${month}-${day}`;
  }

  /** The yyyy-MM-dd an EffectiveFrom names; the API sends it as UTC midnight, which must not be converted. */
  private static calendarDate(valueP: string | null | undefined): string {
    return (valueP ?? '').split('T')[0];
  }

  /** "Friday, May 1, 2026" for a yyyy-MM-dd, built from its parts so no timezone can move it a day. */
  private static longDate(valueP: string | null | undefined): string {
    const [year, month, day] = PayoutRulesComponent.calendarDate(valueP).split('-').map(Number);

    return new Date(year, month - 1, day)
      .toLocaleDateString('en-CA', {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'});
  }

  private static roundToTwo(valueP: number): number {
    return Math.round(valueP * 100) / 100;
  }

  /**
   * Four places, because a share of a fee is two orders of magnitude smaller than the share itself: a third
   * of a 1% fee is 0.3333% of every payment, and the two places the share column stores would print half of
   * a 0.5% fee as 0.25% but a third of it as 0.08%.
   */
  private static roundToFour(valueP: number): number {
    return Math.round(valueP * 10000) / 10000;
  }

  /**
   * The change history as shown, one row per version, with the same summary line the table prints.
   *
   * #1411 asked for an export on "anytime there is a table of information", and this is the table that
   * answers "what were the rules on the day of that payout" — the question effective dating exists for. The
   * effective date is exported as the calendar date it is, never through a timezone conversion: the payout
   * ledger's own bug was a date shifting a day in the reader's zone, and the export is the copy that gets
   * forwarded and argued over.
   */
  private buildVersionsExportRequest(): ITableExportRequest<IGamePayoutRule> {
    return {
      title: 'Payout rule change history',
      subtitle: this.activeGame?.Name ?? '',
      summary: [{label: 'Versions', value: `${this.versions.length}`}],
      rows: this.versions,
      columns: [
        {header: 'In effect from', value: versionP => this.formatEffectiveFrom(versionP.EffectiveFrom)},
        {header: 'Rule', value: versionP => this.versionSummary(versionP)},
        {
          header: 'Voided',
          value: versionP => this.isVoided(versionP) ? `${this.formatVoidedOn(versionP.VoidedOn)}: ${versionP.VoidReason}` : ''
        }
      ],
      fileName: 'payout-rule-history'
    };
  }

  onExportVersionsCsv(): void {
    this.tableExportService.exportCsv(this.buildVersionsExportRequest());
  }

  onExportVersionsPdf(): void {
    this.tableExportService.exportPdf(this.buildVersionsExportRequest());
  }
}
